import "styled-components/macro";

import {
    DropdownButton,
    DropdownButtonIcon,
    DropdownButtonText,
    DropdownButtonTextRow,
    DropdownButtonWrapper,
    DropdownWrapper,
    StyledDropdownIcon,
} from "shared/components/inputs/dropdown/Dropdown.styles";
import React, { useEffect, useState } from "react";

import { AccordionDropdownMultiSelectList } from "shared/components/inputs/dropdown-multi-select/DropdownMultiSelect";
import { AccordionTitle } from "shared/components/data-display/accordion/Accordion.styles";
import DropdownList from "shared/components/inputs/dropdown/DropdownList";
import { FEEDBACK_TYPES } from "shared/config/feedbackTypes";
import PropTypes from "prop-types";
import Tag from "shared/components/data-display/badge/Tag";
import useDropdownListOptions from "shared/components/inputs/dropdown/useDropdownListOptions";
import { useSelect } from "downshift";

const DropdownSingleSelectWithoutSearch = React.forwardRef(
    (
        {
            disabled,
            feedbackType,
            placeholder,
            value,
            displayValueOnButton = false,
            onChange = () => void 0,
            options = [],
            clearDataOnUnmount = false,
            itemsAreLoading = false,
            disabledOptionsList = [],
            disabledOptionTooltipText = "",
            isSmallSize,
            accExpandModeMulti = false,
            accExpandModeMultiInitialVal = false,
            isAccordion = false,
            showIcon = true,
            showIconOnHover = false,
            hideDropDownSearch = false,
            optionVariant = null,
            isShowArrow = false,
            maxHeightOfOptionsList = 215,
            width,
            feedbackMessage,
            position,
            title,
            isSearchInputSmall = false,
            ...rest
        },
        ref
    ) => {
        const {
            filteredOptions,
            filterValue,
            setFilterValue,
            internalFilterUIShown,
        } = useDropdownListOptions(options);

        const [accToggle, setAccToggle] = useState(
            accExpandModeMultiInitialVal
        );

        useEffect(() => {
            if (clearDataOnUnmount) {
                return () => {
                    onChange("");
                };
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally empty: this is an unmount-only cleanup; adding deps would call onChange("") on every re-render
        }, []);

        let showDropDownSearch = internalFilterUIShown;
        if (hideDropDownSearch) {
            showDropDownSearch = false;
        }

        const {
            isOpen,
            selectedItem,
            getToggleButtonProps,
            getMenuProps,
            highlightedIndex,
            getItemProps,
            openMenu,
        } = useSelect({
            items: filteredOptions,
            selectedItem:
                options?.find((option) => option?.value === value) ?? null,
            onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem.value),
        });

        useEffect(() => {
            if (disabled && isOpen) {
                openMenu();
            }
        }, [disabled, isOpen, openMenu]);

        useEffect(() => {
            if (selectedItem && !accExpandModeMulti && isShowArrow) {
                openMenu();
            }
        }, [selectedItem, accExpandModeMulti, isShowArrow, openMenu]);

        const accMultiModeExpandProps = {
            onClick: () => setAccToggle(!accToggle),
        };

        return (
            <DropdownWrapper
                ref={ref}
                isAccordion={isAccordion}
                width={width}
                isAccExpandMode={isAccordion && accExpandModeMulti}
            >
                <DropdownButtonWrapper
                    isDisabled={disabled}
                    className="dropdown-wrapper"
                >
                    <DropdownButton
                        type="button"
                        {...getToggleButtonProps()}
                        {...rest}
                        {...(accExpandModeMulti && accMultiModeExpandProps)}
                        isDisabled={disabled}
                        feedbackType={
                            !isOpen && feedbackMessage
                                ? feedbackType
                                : FEEDBACK_TYPES.None
                        }
                        $hasSelection={!!selectedItem}
                        isAccordion={isAccordion}
                        isSmallSize={isSmallSize}
                    >
                        <DropdownButtonTextRow
                            justify="space-between"
                            isShowArrow={isShowArrow}
                        >
                            <DropdownButtonText isSmallSize={isSmallSize}>
                                {selectedItem &&
                                    displayValueOnButton &&
                                    !isAccordion &&
                                    selectedItem.value}
                                {selectedItem &&
                                    !displayValueOnButton &&
                                    !isAccordion &&
                                    selectedItem.label}
                                {!selectedItem && !isAccordion && placeholder}
                                {isAccordion && (
                                    <AccordionTitle
                                        variant="Headline_5"
                                        show={isOpen}
                                    >
                                        {title}
                                    </AccordionTitle>
                                )}
                            </DropdownButtonText>
                            {isAccordion && selectedItem && (
                                <Tag
                                    label={selectedItem?.value}
                                    handleButtonOnClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )}
                            {isShowArrow && selectedItem && (
                                <StyledDropdownIcon name="arrow-down" size={20} />
                            )}
                            {showIcon && (
                                <DropdownButtonIcon
                                    name="caret-down"
                                    color="Gray_2"
                                    style={{ flexShrink: 0 }}
                                />
                            )}
                            {showIconOnHover && (
                                <DropdownButtonIcon
                                    name={isOpen ? "caret-up" : "caret-down"}
                                    color="Gray_2"
                                    style={{ flexShrink: 0 }}
                                />
                            )}
                        </DropdownButtonTextRow>
                    </DropdownButton>
                </DropdownButtonWrapper>
                {isAccordion && <AccordionDropdownMultiSelectList />}
                <DropdownList
                    isBorder={!isAccordion}
                    isOpen={accExpandModeMulti ? accToggle : isOpen}
                    options={filteredOptions}
                    optionsAreLoading={itemsAreLoading}
                    selectedValue={selectedItem?.value}
                    highlightedIndex={highlightedIndex}
                    getMenuProps={getMenuProps}
                    getItemProps={getItemProps}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    internalFilterUIShown={showDropDownSearch}
                    disabledOptionsList={disabledOptionsList}
                    disabledOptionTooltipText={disabledOptionTooltipText}
                    position={position}
                    optionVariant={optionVariant}
                    maxHeightOfOptionsList={maxHeightOfOptionsList}
                    isSearchInputSmall={isSearchInputSmall}
                />
            </DropdownWrapper>
        );
    }
);

DropdownSingleSelectWithoutSearch.defaultProps = {
    disabled: false,
    feedbackType: FEEDBACK_TYPES.None,
    isSmallSize: false,
    placeholder: "Select",
    width: "100%",
};

DropdownSingleSelectWithoutSearch.propTypes = {
    disabled: PropTypes.bool,
    feedbackType: PropTypes.oneOf(Object.keys(FEEDBACK_TYPES)),
    isSmallSize: PropTypes.bool,
    placeholder: PropTypes.string,
    isSearchInputSmall: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayValueOnButton: PropTypes.bool,
    onChange: PropTypes.func,
    options: PropTypes.array,
    clearDataOnUnmount: PropTypes.bool,
    itemsAreLoading: PropTypes.bool,
    disabledOptionsList: PropTypes.array,
    disabledOptionTooltipText: PropTypes.string,
    accExpandModeMulti: PropTypes.bool,
    accExpandModeMultiInitialVal: PropTypes.bool,
    isAccordion: PropTypes.bool,
    showIcon: PropTypes.bool,
    showIconOnHover: PropTypes.bool,
    hideDropDownSearch: PropTypes.bool,
    optionVariant: PropTypes.string,
    isShowArrow: PropTypes.bool,
    maxHeightOfOptionsList: PropTypes.number,
    width: PropTypes.string,
    feedbackMessage: PropTypes.string,
    position: PropTypes.string,
    title: PropTypes.node,
};

export default DropdownSingleSelectWithoutSearch;
